import { Pause, Play } from 'lucide-react'
import React from 'react'
import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js'
import { IconButton, Row, Show, Text } from '@/components/core'
import { useAutoPlay } from '@/contexts/AutoPlayContext'
import { classNameBuilder } from '@/helpers'

export type AudioPlayerVariant = 'primary' | 'secondary' | 'tertiary'

interface AudioPlayerProps {
  audioUrl?: string
  uniqueAudioUrl?: string
  audioMedia?: HTMLMediaElement | null
  variant?: AudioPlayerVariant
  autoPlay?: boolean
  showControls?: boolean
  playbackSpeed?: number
  containerClassName?: string
  onPause?: () => void
  onFinish?: () => void
  onPlay?: () => void
}

const AudioPlayer = ({
  audioUrl,
  uniqueAudioUrl,
  audioMedia,
  variant = 'primary',
  autoPlay,
  showControls,
  playbackSpeed = 1,
  containerClassName = 'max-h-12',
  onPause,
  onFinish = () => null,
  onPlay
}: AudioPlayerProps) => {
  const waveSurferContainerRef = React.useRef<HTMLDivElement>(null)
  const waveSurferRef = React.useRef<WaveSurfer | undefined>(undefined)
  const [playing, setPlaying] = React.useState<boolean>(false)
  const [audioDuration, setAudioDuration] = React.useState<string>()
  const [remainingAudioDuration, setRemainingAudioDuration] =
    React.useState<string>()

  const { playingUrl } = useAutoPlay()

  const waveSurferOptions = (ref: HTMLDivElement | null) =>
    ({
      container: ref,
      waveColor: variantClassMap[variant].waveColor,
      progressColor: variantClassMap[variant].controlsColor,
      cursorColor: 'transparent',
      barWidth: 4,
      barGap: 4,
      barRadius: 4,
      height: 'auto',
      url: audioMedia ? undefined : audioUrl,
      media: audioMedia,
      dragToSeek: false,
      audioRate: playbackSpeed,
      hideScrollbar: true,
      interact: false
    }) as WaveSurferOptions

  const createWaveSurfer = () => {
    const options = waveSurferOptions(waveSurferContainerRef.current)
    waveSurferRef.current = WaveSurfer.create(options)
  }

  const handlePlayPause = () => {
    setPlaying(!playing)
    waveSurferRef.current?.playPause()
    onPause?.()
  }

  const audioTimerCalculator = (value: number) => {
    const seconds = Math.floor(value % 60)
    const minutes = Math.floor((value / 60) % 60)

    const formattedSeconds = String(seconds).padStart(2, '0')
    const formattedMinutes = String(minutes).padStart(2, '0')

    return formattedMinutes + ':' + formattedSeconds
  }

  React.useEffect(() => {
    if (!waveSurferRef?.current) {
      createWaveSurfer()
    }

    waveSurferRef?.current?.on('ready', () => {
      const getAudioDuration = audioTimerCalculator(
        waveSurferRef.current?.getDuration() || 0
      )
      setAudioDuration(getAudioDuration)
    })
    waveSurferRef?.current?.on('audioprocess', () => {
      const getAudioDuration = waveSurferRef.current?.getDuration() || 0
      const getCurrentTime = waveSurferRef.current?.getCurrentTime() || 0

      const remainingTime = getAudioDuration - getCurrentTime
      const getAudioProgress = audioTimerCalculator(remainingTime)

      setRemainingAudioDuration(getAudioProgress)
    })

    waveSurferRef?.current?.on('play', () => {
      if (onPlay) {
        onPlay()
      }
    })
    waveSurferRef?.current?.on('finish', () => {
      const getAudioDuration = audioTimerCalculator(
        waveSurferRef.current?.getDuration() || 0
      )
      setPlaying(false)
      setAudioDuration(getAudioDuration)
      if (onFinish) {
        onFinish()
      }
    })

    return () => {
      waveSurferRef?.current?.unAll()
    }
  }, [])

  React.useEffect(() => {
    if (
      waveSurferRef.current &&
      !playing &&
      autoPlay &&
      playingUrl === uniqueAudioUrl
    ) {
      waveSurferRef.current.play()
      setPlaying(true)
    }
  }, [playingUrl, autoPlay, uniqueAudioUrl])

  React.useEffect(() => {
    return () => {
      if (autoPlay && uniqueAudioUrl === playingUrl) {
        waveSurferRef.current?.stop()
        onFinish()
      }
    }
  }, [playingUrl])

  return (
    <Row className={`h-fit w-full items-center space-x-2`}>
      {showControls && (
        <IconButton
          size="40"
          variant="ghost"
          icon={
            <Show
              when={playing}
              fallback={
                <Play
                  color={variantClassMap[variant].controlsColor}
                  size={32}
                  strokeWidth={2}
                  className="pl-1"
                />
              }
            >
              <Pause
                color={variantClassMap[variant].controlsColor}
                size={32}
                strokeWidth={2}
                className="py-0.5"
              />
            </Show>
          }
          onClick={handlePlayPause}
        />
      )}
      <div
        ref={waveSurferContainerRef}
        className={classNameBuilder(
          'h-[80%] w-full self-center',
          containerClassName
        )}
      />
      <Text
        size="xs"
        className={`${variantClassMap[variant].timerColor} w-10 text-center`}
      >
        <Show when={playing} fallback={audioDuration}>
          {remainingAudioDuration}
        </Show>
      </Text>
    </Row>
  )
}

export default AudioPlayer

type AudioPlayerColorKey =
  | 'backgroundColor'
  | 'controlsColor'
  | 'waveColor'
  | 'timerColor'

export const variantClassMap: Record<
  AudioPlayerVariant,
  Record<AudioPlayerColorKey, string>
> = {
  primary: {
    backgroundColor: 'bg-white',
    controlsColor: '#280166',
    waveColor: '#D4CCE0',
    timerColor: '!text-electric-indigo-900'
  },
  secondary: {
    backgroundColor: 'bg-electric-indigo-50/50',
    controlsColor: '#280166',
    waveColor: '#CFCAD5',
    timerColor: '!text-gray-400'
  },
  tertiary: {
    backgroundColor: 'bg-electric-indigo-900',
    controlsColor: '#FFFFFF',
    waveColor: '#4F3580',
    timerColor: '!text-white'
  }
}
